/**
 * PermissionRow Component
 * 
 * Displays a single permission assignment row in the permissions table.
 */

import { Button, Icon, Badge, TableRow, TableCell } from '@/components/ui';
import { useI18n } from '@/i18n';
import type { PermissionAssignment } from '../types';

interface PermissionRowProps {
  permission: PermissionAssignment;
  onRevoke: () => void;
  isRevoking?: boolean;
}

/**
 * Get display name for permission level
 */
function getPermissionLabel(permission: string, t: (key: string) => string): string {
  return t(`admin.permissions.levels.${permission}`) || permission;
}

/**
 * Get badge variant based on permission level
 */
function getPermissionBadgeClass(permission: string): string {
  switch (permission) {
    case 'admin':
      return 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300';
    case 'edit':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300';
    case 'use':
      return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300';
    case 'view':
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  }
}

export function PermissionRow({ permission, onRevoke, isRevoking }: PermissionRowProps) {
  const { t } = useI18n();
  
  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <Icon 
              name={permission.subjectType === 'user' ? 'users' : 'shield'} 
              size={16} 
              className="text-gray-500 dark:text-gray-400"
            />
          </div>
          <div className="flex flex-col">
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {permission.subjectName}
            </span>
            {permission.subjectEmail && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {permission.subjectEmail}
              </span>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
          {permission.subjectType === 'user' 
            ? t('admin.permissions.typeUser') || 'User'
            : t('admin.permissions.typeGroup') || 'Group'
          }
        </span>
      </TableCell>
      <TableCell>
        <Badge className={getPermissionBadgeClass(permission.permission)}>
          {getPermissionLabel(permission.permission, t)}
        </Badge>
      </TableCell>
      <TableCell>
        <Button
          variant="ghost"
          size="sm"
          leftIcon="trash"
          onClick={onRevoke}
          disabled={isRevoking}
          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20"
        >
          {t('common.delete')}
        </Button>
      </TableCell>
    </TableRow>
  );
}
